import moment, { Moment } from 'moment-timezone';

const JST = 'Asia/Tokyo';

const toJstCalendarDay = (date: Moment) => moment.tz([date.year(), date.month(), date.date()], JST);

/**
 * Selected expiry date -> stored value (next day 00:00:00 JST).
 * Built from the calendar day the user saw rather than the instant, because the date picker
 * returns a value in the browser's timezone.
 */
export const toPointExpiresAtParam = (date: Moment | null | undefined): Moment | null =>
  date ? toJstCalendarDay(date).add(1, 'day') : null;

/**
 * Stored value -> selected expiry date, stepping back a day only at exactly 00:00:00 JST.
 * Next-day-00:00:00 is an admin-UI convention; values registered through the Partner API are
 * absolute, so subtracting a day unconditionally shows them a day before they really expire.
 */
export const toPointExpiresAtFormValue = (
  value: string | Moment | null | undefined
): Moment | null => {
  if (value == null) return null;

  const jst = moment(value).tz(JST);
  // Milliseconds are ignored: the old check-side conversion kept them, storing values like
  // 00:00:00.345, which a strict zero check would fail to recognise as admin-written
  const isJstMidnight = jst.hours() === 0 && jst.minutes() === 0 && jst.seconds() === 0;

  return isJstMidnight ? jst.subtract(1, 'day').endOf('day') : jst;
};

/**
 * Compared at second precision: Partner-API values are stored as 23:59:59.000 while the picker
 * writes endOf('day') (23:59:59.999), so a millisecond compare would call a same-day re-pick an
 * edit and rewrite the stored value.
 */
export const isSamePointExpiresAt = (
  a: Moment | null | undefined,
  b: Moment | null | undefined
): boolean => (a == null || b == null ? a == null && b == null : a.isSame(b, 'second'));

/**
 * True when saving would rewrite the time of day the picker is showing.
 * The date picker edits a day, not a time, and the save stores the end of the selected day, so a
 * value registered through the Partner API with an arbitrary time silently moves when re-picked.
 */
export const willRepickChangePointExpiresAt = (value: Moment | null | undefined): boolean =>
  value != null && !moment(value).tz(JST).isSame(moment(value).tz(JST).endOf('day'), 'second');
